import { dec, UNITS, ZERO } from "@bl/domain";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, BookOpen, Flame, Printer, Trash2 } from "lucide-react";
import { useEffect, useId, useState } from "react";
import {
  type BrewDetail,
  type BrewSummary,
  send,
  useBrewSeries,
  useChange,
  useRecipe,
} from "../api";
import { inUnit, repitches, unitOf } from "../brewPulls";
import { PrintSheets } from "../components/PrintSheets";
import {
  Dialog,
  DialogForm,
  ErrorNote,
  Toolbar,
  ToolbarButton,
  ToolbarSeparator,
} from "../components/ui";
import { showDate } from "../format";
import { useNotify } from "../notify";
import { showGallons } from "../recipeFormat";
import { repeatLabel } from "../recurrence";
import { BrewTanks, TakeOffDialog } from "./BrewTanks";
import { whenOf } from "./PullCard";
import { beerName } from "./Repitch";
import { PlanBrewDialog, SeriesMark, VersionField } from "./Schedule";

const STATUS_CHIPS: Record<BrewSummary["status"], { tone: string; label: string }> = {
  planned: { tone: "plain", label: "Planned" },
  draft: { tone: "accent", label: "In progress" },
  brewed: { tone: "ok", label: "Brewed" },
};
/** Planned, in progress, brewed, or brewed with stock still to give its lot, which flags it until it is. */
export const StatusChip = ({ brew: b }: { brew: Pick<BrewSummary, "status" | "toAssign"> }) =>
  b.status === "brewed" && b.toAssign.length ? (
    <span className="chip warn">Stock to assign</span>
  ) : (
    <span className={`chip ${STATUS_CHIPS[b.status].tone}`}>{STATUS_CHIPS[b.status].label}</span>
  );
/**
 * A brew's day, planned or in progress, which moves its tank bookings with it: it moves once the field
 * is left or Enter is pressed, not after each part typed (the re-critique,
 * 2026-10-07: brew day's date still saved as it changed, so a year typed a digit at a time moved the
 * brew through each year on its way).
 */
export function BrewDayField({
  id,
  value,
  disabled,
  onDay,
}: {
  id: string;
  value: string;
  disabled: boolean;
  onDay: (day: string) => void;
}) {
  const [day, setDay] = useState(value);
  useEffect(() => setDay(value), [value]);
  const commit = () => {
    if (day && day !== value) onDay(day);
  };
  return (
    <input
      id={id}
      className="input"
      type="date"
      value={day}
      disabled={disabled}
      onChange={(ev) => setDay(ev.target.value)}
      onBlur={commit}
      onKeyDown={(ev) => {
        if (ev.key === "Enter") commit();
      }}
    />
  );
}
/**
 * A brew on the schedule: a day and a recipe, no lots chosen and nothing taken (Tyler, 2026-10-02).
 * It shows what the version it brews (the newest, or one it keeps) needs against what is on hand;
 * whether the brews before it leave enough is To order's to say. Starting brew day chooses the lots.
 */
export function PlannedBrew({ brew: b }: { brew: BrewDetail }) {
  const dayId = useId();
  const navigate = useNavigate();
  const notify = useNotify();
  const [removing, setRemoving] = useState(false);
  // This brew's version, and the recurring brew it is one of (Tyler, 2026-10-03): either can be
  // changed for this brew alone, or the series changed or taken off as a whole.
  const { data: recipe } = useRecipe(b.recipe.id);
  const { data: series } = useBrewSeries(b.seriesId);
  const [changingSeries, setChangingSeries] = useState(false);
  const [removingSeries, setRemovingSeries] = useState(false);
  const toCome = (series?.brews ?? []).filter((x) => x.status === "planned").length;
  const version = useChange((kept: string, key) =>
    send.saveBrew(
      { id: b.id, patch: kept ? { recipeId: kept, keepVersion: true } : { keepVersion: false } },
      key,
    ),
  );
  const removeSeries = useChange((_: undefined, key) => send.deleteSeries(b.seriesId ?? "", key), {
    onSuccess: ({ deleted }) => {
      notify(
        `${b.recipe.name}: ${deleted} ${deleted === 1 ? "brew" : "brews"} taken off the calendar`,
      );
      navigate({ to: "/production" });
    },
  });
  const move = useChange((brewedOn: string, key) =>
    send.saveBrew({ id: b.id, patch: { brewedOn } }, key),
  );
  const start = useChange((_: undefined, key) => send.startBrewDay(b.id, key), {
    onSuccess: () => notify({ title: "Brew day started", link: { kind: "brew", brewId: b.id } }),
  });
  return (
    <>
      <Toolbar
        left={
          <>
            <Link to="/production" className="toolbar-button" title="Back to Production">
              <ArrowLeft size={20} />
              <span className="label">Production</span>
            </Link>
            <ToolbarSeparator />
          </>
        }
        right={
          <>
            <Link
              to="/recipes/$recipeId"
              params={{ recipeId: b.recipe.id }}
              className="toolbar-button"
              title="The recipe"
            >
              <BookOpen size={20} />
              <span className="label">Recipe</span>
            </Link>
            <PrintButton />
            <ToolbarButton
              icon={<Trash2 size={20} />}
              label="Take off"
              title="Take it off the calendar"
              onClick={() => setRemoving(true)}
            />
          </>
        }
      />
      <div className="page-body brew-page">
        <section className="brew-card brew-hero">
          <div className="brew-hero-head">
            <div>
              <div className="muted">
                Planned
                {b.recipe.version > 1 ? `, version ${b.recipe.version}` : ""}
                {`, ${showGallons(b.batchLiters)}`}
              </div>
              <h1>
                {b.recipe.name}
                {b.seriesId ? <SeriesMark /> : null}
              </h1>
            </div>
            <StatusChip brew={b} />
          </div>
          <div className="brew-field">
            <label htmlFor={dayId}>Brew day</label>
            <BrewDayField
              id={dayId}
              value={b.brewedOn}
              disabled={move.isPending}
              onDay={(day) => move.mutate(day)}
            />
          </div>
          <VersionField
            versions={recipe?.versions ?? []}
            value={b.keepsVersion ? b.recipe.id : ""}
            disabled={version.isPending}
            onChange={(kept) => version.mutate(kept)}
          />
          <button
            type="button"
            className="button primary"
            disabled={start.isPending}
            onClick={() => start.mutate(undefined)}
          >
            <Flame size={16} /> Start brew day
          </button>
          <p className="faint">
            Its lots are chosen, oldest first, when brew day starts. Until then nothing is set
            aside, and <Link to="/to-order">To order</Link> counts what it needs.
          </p>
          <ErrorNote error={move.error ?? start.error ?? version.error} />
        </section>

        {series ? (
          <section className="brew-card series-card">
            <div className="series-card-head">
              <strong>
                Recurring <SeriesMark />
              </strong>
            </div>
            <p className="muted">
              {repeatLabel(series.repeat, series.firstDay)}
              {series.until ? `, until ${showDate(series.until)}` : ""}. {toCome}{" "}
              {toCome === 1 ? "brew" : "brews"} to come
              {series.keepsVersion ? `, each brewing version ${series.recipe.version}` : ""}.
            </p>
            <div className="row">
              <button type="button" className="button" onClick={() => setChangingSeries(true)}>
                Change the series
              </button>
              <button type="button" className="button" onClick={() => setRemovingSeries(true)}>
                Take the series off
              </button>
            </div>
          </section>
        ) : null}

        <h2 className="brew-heading">Tanks</h2>
        <BrewTanks brew={b} />

        <h2 className="brew-heading">What it needs</h2>
        <div className="brew-card">
          <ul className="planned-needs">
            {b.lines.map((l) => {
              // A yeast's own stock, and its strain's harvest beside it, which is weighed.
              const onHand = l.options
                .filter((o) => !repitches(l, o))
                .reduce((n, o) => n.plus(dec(o.onHand)), ZERO);
              const harvested = l.options
                .filter((o) => repitches(l, o))
                .reduce((n, o) => n.plus(dec(o.onHand)), ZERO);
              const clean = l.repitchFrom.find((x) => x.warnings.length === 0);
              const unit = unitOf(l);
              return (
                <li key={l.recipeLineId}>
                  <span>
                    {l.item?.name ?? l.name}
                    {whenOf(l) ? <span className="faint"> ({whenOf(l)})</span> : null}
                  </span>
                  <span className="num">
                    {l.needed === null ? (
                      <span className="faint">{l.item ? "no amount" : "not linked"}</span>
                    ) : (
                      `${inUnit(l.needed, unit)} ${UNITS[unit].label}`
                    )}
                  </span>
                  <span className="faint num">
                    {l.item
                      ? `${inUnit(onHand.toString(), unit)} ${UNITS[unit].label} on hand${
                          harvested.gt(ZERO)
                            ? `, ${inUnit(harvested.toString(), "lb")} lb harvested`
                            : ""
                        }`
                      : ""}
                  </span>
                  {/* Its yeast from a beer in a fermenter then, so it asks for no fresh pitch. */}
                  {clean ? (
                    <span className="faint planned-repitch">
                      Repitch from {clean.tankName}, {beerName(clean)}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      <PrintSheets recipe={recipe} brew={b} />
      <TakeOffDialog
        open={removing}
        brewId={b.id}
        onClose={() => setRemoving(false)}
        onDone={() => navigate({ to: "/production" })}
      />
      {series ? (
        <PlanBrewDialog
          open={changingSeries}
          onClose={() => setChangingSeries(false)}
          series={series}
          onSeriesSaved={(saved) => {
            // This brew's own day dropped from the series takes it off; Production shows the rest.
            if (!saved.brews.some((x) => x.id === b.id)) navigate({ to: "/production" });
          }}
        />
      ) : null}
      <Dialog
        open={removingSeries}
        onClose={() => setRemovingSeries(false)}
        title="Take the series off the calendar?"
      >
        <DialogForm
          onSubmit={() => removeSeries.mutate(undefined)}
          footer={
            <>
              <button type="button" className="button" onClick={() => setRemovingSeries(false)}>
                Keep it
              </button>
              <button type="submit" className="button danger" disabled={removeSeries.isPending}>
                Take the series off
              </button>
            </>
          }
        >
          <p>
            The {toCome} {toCome === 1 ? "brew" : "brews"} of this recurring brew still to come
            {toCome === 1 ? " comes" : " come"} off the calendar, this one among them. Brews already
            started or brewed stay as they are.
          </p>
          <ErrorNote error={removeSeries.error} />
        </DialogForm>
      </Dialog>
    </>
  );
}
/**
 * The brew's Brew Log and its recipe on paper, for the tank's clipboard (Scott's Jam, 2026-10-08): what
 * it has recorded is filled in, its day, number and tanks among it, and the rest left to write on.
 */
export const PrintButton = () => (
  <ToolbarButton
    icon={<Printer size={20} />}
    label="Print"
    title="Print its Brew Log and recipe sheet"
    onClick={() => window.print()}
  />
);
